import data, { Emoji as IEmoji } from '@emoji-mart/data';
import { ListItemAvatar, ListItemText, MenuItem } from '@mui/material';
import { PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import { SuggestionProps } from '@tiptap/suggestion';

import { emojiExtension } from '@copera/tiptap/extensions';
import { SearchIndex, init } from 'emoji-mart';
import { useCallback } from 'react';
import { CustomMenu } from '../private/components/CustomMenu';

init({ data });

const EmojiPicker = ({
  items,
  command,
  clientRect,
  editor,
  onClose,
}: SuggestionProps<IEmoji> & { onClose(): void }) => {
  const selectItem = useCallback(
    (index: number) => {
      command(items[index]);
      onClose();
    },
    [items, command, onClose],
  );

  if (!items?.length) {
    return null;
  }

  return (
    <CustomMenu
      {...{ clientRect, editor, onClose, selectItem, totalItems: items.length }}
    >
      {({ selectedIndex }) =>
        items?.map((item, index) => (
          <MenuItem
            dense
            key={item.id}
            selected={selectedIndex === index}
            onClick={() => {
              command(item);
              onClose();
            }}
          >
            <ListItemAvatar>{item.skins?.[0].native}</ListItemAvatar>
            <ListItemText primary={item.name} secondary={`:${item.id}:`} />
          </MenuItem>
        ))
      }
    </CustomMenu>
  );
};

export const EmojiSearch = emojiExtension.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      suggestion: {
        pluginKey: new PluginKey('emojisearch'),
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: 'emoji-search', attrs: props },
              { type: 'text', text: ' ' },
            ])
            .run();
        },
      },
    };
  },
  renderText({ node }) {
    const emoji: IEmoji = (SearchIndex as any).get(node.attrs.id);
    if (!emoji) return `:${node.attrs.id}:`;

    return emoji.skins?.[0]?.native || `:${node.attrs.id}:`;
  },
});

export const emojiSearchExtension = EmojiSearch.configure({
  suggestion: {
    char: ':',
    items: async ({ query }) => {
      if (!query) {
        return [];
      }

      // attempts to match for emoji like search
      if (query.length === 1) {
        return SearchIndex.search(`:${query}`);
      }

      return SearchIndex.search(query);
    },
    render() {
      let component: ReactRenderer;

      const onClose = () => {
        component?.destroy();
      };

      return {
        onStart(props) {
          component = new ReactRenderer(EmojiPicker, {
            props: { onClose, ...props },
            editor: props.editor,
          });
        },
        onUpdate(props) {
          component.updateProps(props);
        },
        onExit() {
          onClose();
        },
      };
    },
  },
});
